// "Install Cercle" as a bottom sheet, opened from the storage notice on the
// dashboard and from Settings. Where the browser fired `beforeinstallprompt`
// (Chromium desktop/Android), a single button triggers the native dialog.
// Everywhere else — notably Safari, which never fires that event — falls back
// to a short per-platform written guide, defaulted to the visitor's own
// device but switchable, since someone might be reading it on one device to
// install on another.

import { h } from './dom';
import { toast } from './toast';
import { primaryButton } from './components';
import { canPromptInstall, detectPlatform, triggerInstallPrompt, type Platform } from '../core/installPrompt';

const GUIDES: Record<Platform, { label: string; steps: string[]; note?: string }> = {
  ios: {
    label: 'iPhone / iPad',
    steps: [
      'Ouvrez ce lien dans Safari (obligatoire, même si vous l’avez reçu ailleurs).',
      'Appuyez sur l’icône Partager (le carré avec une flèche vers le haut), en bas de l’écran.',
      'Faites défiler la liste et choisissez « Sur l’écran d’accueil ».',
      'Appuyez sur Ajouter, en haut à droite.',
    ],
  },
  android: {
    label: 'Android',
    steps: [
      'Appuyez sur le menu ⋮ en haut à droite du navigateur.',
      'Choisissez « Installer l’application » (ou « Ajouter à l’écran d’accueil »).',
      'Confirmez avec Installer.',
    ],
  },
  desktop: {
    label: 'Ordinateur',
    steps: [
      'Cliquez sur l’icône d’installation ⊕ dans la barre d’adresse, à droite.',
      'Si vous ne la voyez pas : menu ⋮ → « Installer Cercle… ».',
      'Confirmez avec Installer.',
    ],
    note: 'Sur Safari (Mac) : menu Fichier → Ajouter au Dock. Firefox ne propose pas encore l’installation : utilisez Chrome ou Edge.',
  },
};

const PLATFORM_ORDER: Platform[] = ['ios', 'android', 'desktop'];

/** Open the install sheet. Same overlay pattern as addFriendSheet: a transient
 * element appended to <body>, independent of the router. */
export function openInstallSheet(): void {
  const scrim = h('div', { class: 'sheet-scrim' });
  const handle = h('div', { class: 'sheet-handle' });
  const title = h('h2', { class: 'sheet-title', text: 'Installer Cercle' });
  const overlay = h('div', { class: 'sheet-overlay' });

  let closing = false;
  const close = (): void => {
    if (closing) return;
    closing = true;
    document.removeEventListener('keydown', onKey);
    overlay.classList.remove('is-open');
    setTimeout(() => overlay.remove(), 320);
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') close();
  };
  scrim.addEventListener('click', close);
  document.addEventListener('keydown', onKey);

  const handleInstall = async (): Promise<void> => {
    const accepted = await triggerInstallPrompt();
    close();
    toast(accepted ? 'Installation en cours…' : 'Vous pourrez installer l’app plus tard');
  };

  const sheet = h(
    'div',
    { class: 'sheet', role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Installer Cercle' },
    handle,
    title,
    h('p', {
      class: 'lead',
      text: 'Sur votre écran d’accueil, Cercle s’ouvre en plein écran et vos données ne risquent plus d’être effacées par le navigateur.',
    }),
  );

  if (canPromptInstall()) {
    sheet.append(
      primaryButton('Installer l’app', () => void handleInstall(), 'download'),
      h('p', { class: 'small muted center', text: 'Ou installez manuellement :' }),
    );
  }

  const tabsRow = h('div', { class: 'chips' });
  const stepsList = h('ol', { class: 'install-steps' });
  const noteEl = h('p', { class: 'small muted' });
  let active = detectPlatform();

  const renderSteps = (): void => {
    const guide = GUIDES[active];
    stepsList.replaceChildren(...guide.steps.map((s) => h('li', { text: s })));
    noteEl.hidden = !guide.note;
    if (guide.note) noteEl.textContent = guide.note;
  };
  const renderTabs = (): void => {
    tabsRow.replaceChildren(
      ...PLATFORM_ORDER.map((p) =>
        h(
          'button',
          {
            class: 'chip-filter' + (active === p ? ' is-active' : ''),
            type: 'button',
            onclick: () => {
              active = p;
              renderTabs();
              renderSteps();
            },
          },
          GUIDES[p].label,
        ),
      ),
    );
  };
  renderTabs();
  renderSteps();

  sheet.append(tabsRow, stepsList, noteEl);
  overlay.append(scrim, sheet);
  document.body.append(overlay);
  requestAnimationFrame(() => overlay.classList.add('is-open'));
}
